import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { JitsiTrackEvents } from '../../../lib-jitsi-meet';
import FullScreenBtn from './FullScreenBtn';
import { toggleLoad } from '../../../loading';
function getRandomIntervalInMs() {
    // 3분에서 5분 사이의 밀리초 범위
    const minIntervalMs = 2 * 60 * 1000; // 3분 = 180초 = 180,000밀리초
    const maxIntervalMs = 4 * 60 * 1000; // 5분 = 300초 = 300,000밀리초
    // minIntervalMs와 maxIntervalMs 사이의 랜덤한 밀리초를 생성
    const randomMs = Math.floor(Math.random() * (maxIntervalMs - minIntervalMs + 1)) + minIntervalMs;
    return randomMs;
}
// 전체 재접속(5초 워치독) 전역 쿨다운. restartMediaSessions 는 실질적으로 jitsi 전체 재접속이라
// 방 전체에 퇴장+입장 이벤트와 키프레임 펄스를 재발생시키는 증폭기다(운영 부하 감사 R4).
// 혼잡 순간의 연쇄 재접속 폭주를 막기 위해 클라이언트당 최소 간격을 강제한다.
const MEDIA_RESTART_COOLDOWN_MS = 60000;
let lastMediaRestartAt = 0;
const freezeRecoveryByParticipant = new Map();
const getFreezeRecovery = (participantId) => {
    const key = participantId || '__unknown__';
    let rec = freezeRecoveryByParticipant.get(key);
    if (!rec) {
        rec = { stage: 0, stageAt: 0, restarts: 0, lastRestartAt: 0, lastFrame: null, recovering: false };
        freezeRecoveryByParticipant.set(key, rec);
    }
    return rec;
};
const FREEZE_DETECT_MS = 5000; // 무프레임 판정
const FREEZE_REATTACH_WAIT_MS = 5000; // 재부착 후 프레임 대기
const FREEZE_LIGHT_WAIT_MS = 20000; // 가벼운 세션 재시작 후 프레임 대기
const FREEZE_FULL_WAIT_MS = MEDIA_RESTART_COOLDOWN_MS; // 전체 재접속 후 프레임 대기
const MAX_FREEZE_RESTARTS = 3; // 참가자당 전체 재접속 상한
const FREEZE_RESTART_DECAY_MS = 180000; // 마지막 전체 재접속 뒤 이 시간 동안 정상이면 누적 초기화
const FREEZE_IDLE_LIGHT_RETRY_MS = 180000; // 상한 도달(대기 모드) 뒤 가벼운 재시작 재시도 간격
const FREEZE_TEXT_WAIT = '영상 신호를 기다리는 중...';
const FREEZE_TEXT_RECOVERING = '영상 연결을 복구하는 중...';
const FREEZE_TEXT_SENDER = '강사 영상을 받지 못하고 있습니다. 강사 측 송출을 확인하는 중입니다.';
const Video = React.memo((props) => {
    const dispatch = useDispatch();
    const fullscreenElement = useRef(null);
    const videoElement = useRef(null);
    // 워터마크 interval 은 인스턴스 ref 에 둔다 — 종전 모듈 전역 변수는 타일 2개 이상에서 뒤 타일이 앞 id 를
    // 덮어 앞 interval 이 영구 누수되고, 앞 타일 unmount 시 뒤 타일 워터마크가 꺼졌다(유출추적 결함).
    const watermarkTimer = useRef(null);
    const [videoStyle, setVideoStyle] = useState({ width: 0, height: 0 });
    const [trackIsSet, setTrackIsSet] = useState(false);
    // 타일 내 대기 표시 문구(false=숨김) — 핀(다른 학생) 타일의 첫 프레임 대기, 그리고
    // 비-rVFC 폴백의 재접속 쿨다운 대기(검은 타일 방치 방지)에 함께 쓴다
    const [peerWaiting, setPeerWaiting] = useState(false);
    const peerWaitingRef = useRef(false);
    const clearPeerWaiting = () => {
        if (peerWaitingRef.current) {
            peerWaitingRef.current = false;
            setPeerWaiting(false);
        }
    };
    const _attachTrack = (videoTrack) => {
        if (!videoElement?.current)
            return;
        if (!videoTrack || !videoTrack.track) {
            return;
        }
        videoTrack.track.attach(videoElement?.current);
        setTrackIsSet(true);
        // onVideoPlaying();
    };
    // (구 _detachTrack 제거됨 — cleanup 이 setup 시점 캡처 요소를 직접 detach 한다.
    //  ref 기준 detach 는 unmount 시 ref 가 이미 null 이라 항상 no-op = containers 누수였음)
    const rePlayVideo = () => {
        if (videoElement?.current) {
            videoElement?.current.play().catch(err => {
                if (err?.name !== 'AbortError')
                    console.error('Error replaying video:', err);
            });
        }
    };
    useEffect(() => {
        // srcObject(MediaStream) 교체는 그 자체로 media element load algorithm 을 실행하므로(WHATWG)
        // 명시적 pause()→load() 는 중복이며, readyState 를 HAVE_NOTHING 으로 리셋해 첫 프레임 대기를
        // 재유발한다. 기존의 load()+1초 지연 play 는 재부착마다 ≥1초 검은 화면을 스스로 만들어
        // 아래 1초 로딩 워치독과 정확히 경합했다(전체화면 로딩 오탐의 직접 원인). 재생 보장만 남긴다.
        if (videoElement.current && props.videoTrack && trackIsSet && videoElement.current.paused) {
            videoElement.current.play().catch(err => {
                // AbortError(요소가 DOM 에서 제거됨)는 리마운트 경합 시 발생하는 무해한 케이스 — 무시
                if (err?.name !== 'AbortError')
                    console.error('Error playing video:', err);
            });
        }
        // ★stream.id 를 deps 에 포함: 재협상으로 stream 이 교체(동일 요소 srcObject 스왑)될 때
        // (videoTrack 객체는 그대로여도) 재생을 다시 보장한다. load algorithm 리셋 후 autoplay
        // 폴백이 실패하는 브라우저 대비.
    }, [props.videoTrack, trackIsSet, props.videoTrack?.track?.stream?.id]);
    useEffect(() => {
        const visibilitychange = () => {
            if (document.hidden) {
                videoElement?.current && videoElement?.current?.play();
            }
            else {
                rePlayVideo();
            }
        };
        document.addEventListener('visibilitychange', visibilitychange);
        // iOS: BT 등 오디오 라우트 변경 시 iOS 가 라이브 영상도 자동 일시정지(재개 안 함)하는 경우 재생 재개.
        // 라이브 원격 영상은 사용자가 의도적으로 멈추지 않으므로 안전. pause→play 폭주 방지 400ms 쿨다운.
        const pauseEl = videoElement.current;
        let lastResumeTs = 0;
        const onPause = () => {
            if (document.hidden || !pauseEl || pauseEl.ended)
                return;
            const now = Date.now();
            if (now - lastResumeTs < 400)
                return;
            lastResumeTs = now;
            pauseEl.play().catch(() => { });
        };
        pauseEl && pauseEl.addEventListener('pause', onPause);
        const resizeObserver = new ResizeObserver(entries => {
            for (let entry of entries) {
                const { width, height } = entry.contentRect;
                if (!width && !height)
                    return;
                setVideoStyle({ width, height });
            }
        });
        if (videoElement?.current) {
            resizeObserver.observe(videoElement?.current);
        }
        return () => {
            if (resizeObserver && videoElement?.current) {
                resizeObserver.unobserve(videoElement?.current);
            }
            pauseEl && pauseEl.removeEventListener('pause', onPause);
            document.removeEventListener('visibilitychange', visibilitychange);
        };
    }, []);
    useEffect(() => {
        const element = videoElement.current;
        if (element) {
            element.volume = 0;
            element.autoplay = true;
            element.playsInline = true;
        }
        _attachTrack(props.videoTrack);
        // 만약 비디오 트랙이 멈췄을때 재 시작 할수 있도록
        // @ts-ignore
        props.videoTrack && props.videoTrack?.track.on(JitsiTrackEvents.LOCAL_TRACK_STOPPED, rePlayVideo);
        return () => {
            // ★setup 시점에 캡처한 요소(element)를 detach 한다 — videoElement.current 는 요소가
            // 교체(리마운트)된 뒤에는 "신 요소"를 가리키므로, current 기준 detach 는 신 요소를
            // 잘못 detach 하고 구 요소는 track.containers 에 영구 누적(구 MediaStream 핀)되는
            // 누수가 있었다. 캡처 요소 기준이면 요소 교체 여부와 무관하게 항상 올바른 짝을 푼다.
            if (element) {
                try {
                    props.videoTrack?.track?.detach(element);
                }
                catch (e) { /* 이미 정리된 트랙/요소 — 무시 */ }
            }
            // @ts-ignore
            props.videoTrack && props.videoTrack?.track.off(JitsiTrackEvents.LOCAL_TRACK_STOPPED, rePlayVideo);
        };
        // ★stream.id 를 deps 에 포함(검은 화면 버그 수정): 재협상으로 stream 이 교체될 때
        // videoTrack redux 객체가 그대로면 이 effect 가 재실행되지 않아 새 스트림이 요소에
        // 안 붙어 검은 화면이 됐다. stream.id 변경 시 같은 요소에 재부착(+autoplay 재설정)한다.
        // (key 리마운트 제거 후에는 이 deps 가 유일한 스트림 교체 반영 경로다)
    }, [props.videoTrack, props.videoTrack?.track?.stream?.id]);
    useEffect(() => {
        watermarkTimer.current =
            props.nickname && props.showWaterMark
                ? setInterval(() => {
                    if (!videoElement.current)
                        return;
                    const { offsetWidth: width, offsetHeight: height } = videoElement.current;
                    // Create and style the canvas element
                    const canvasElement = document.createElement('canvas');
                    canvasElement.style.position = 'absolute';
                    canvasElement.style.top = '0';
                    canvasElement.style.left = '0';
                    canvasElement.style.width = '100%';
                    canvasElement.style.height = '100%';
                    canvasElement.width = width;
                    canvasElement.height = height;
                    canvasElement.style.zIndex = '3';
                    const ctx = canvasElement.getContext('2d');
                    const fontSize = 25;
                    if (ctx) {
                        const devicePixelRatio = window.devicePixelRatio || 1;
                        ctx.scale(devicePixelRatio, devicePixelRatio);
                        ctx.setTransform(1, 0, 0, 1, 0, 0);
                        const watermarkText = props.nickname || '대성마이맥';
                        ctx.font = `${fontSize}px Arial`;
                        ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
                        // Calculate text width and height
                        const textWidth = ctx.measureText(watermarkText).width * 2;
                        const textHeight = 25;
                        // Determine random position for the watermark
                        const x = Math.max(Math.min(Math.random() * (width - textWidth), width - textWidth - 25), 25);
                        const y = Math.max(Math.min(Math.random() * (height - textHeight), height - textHeight - 25), 25);
                        // Draw the watermark
                        ctx.fillText(watermarkText, x, y);
                        if (fullscreenElement.current) {
                            fullscreenElement.current.appendChild(canvasElement);
                        }
                    }
                    // Remove the canvas after 1 second
                    setTimeout(() => {
                        if (fullscreenElement.current && canvasElement) {
                            fullscreenElement.current.removeChild(canvasElement);
                        }
                    }, 1000);
                }, getRandomIntervalInMs())
                : null;
        return () => {
            if (watermarkTimer.current)
                clearInterval(watermarkTimer.current);
            watermarkTimer.current = null;
        };
    }, [props.nickname, props.showWaterMark]);
    const canPlayStartTime = useRef(null);
    const didFireRef = useRef(false);
    const loadingShownRef = useRef(false);
    useEffect(() => {
        // @ts-ignore
        if (props.videoTrack?.local)
            return; // 로컬 트랙은 처리하지 않음
        let canPlayTimeout = null;
        let showLoadTimeout = null;
        didFireRef.current = false; // Reset on new videoTrack
        loadingShownRef.current = false; // Reset on new videoTrack
        clearPeerWaiting(); // Reset on new videoTrack
        canPlayStartTime.current = Date.now();
        // 실제 프레임 렌더 감지(rVFC). canplaythrough 는 readyState 가 HAVE_ENOUGH_DATA 에 "새로"
        // 도달할 때만 발화하므로, load() 리셋 없이 effect 만 재실행되면(트랙 객체 교체 등) 영상이
        // 멀쩡히 나오는데도 이벤트가 다시 오지 않아 워치독이 오탐한다(멀쩡한 영상에 전체 재접속).
        // 컴포지터에 프레임이 실제 제출되는 것을 체인 등록으로 지속 관찰해 오탐을 차단하고,
        // 마지막 프레임 시각으로 "재생 중 프리즈"(첫 프레임 이후 스트림 정지)도 감지한다.
        // (Chrome 83+/Safari 15.4+ — 미지원 브라우저는 기존 동작 그대로, 프리즈 감지만 비활성)
        const watchedEl = videoElement.current;
        let rvfcId = null;
        let lastFrameTs = Date.now();
        // 참가자(송신자) 단위 복구 레코드 — 트랙 교체로 effect 가 재실행돼도 이어진다(모듈 상단 설명 참고)
        // @ts-ignore
        const participantId = props.videoTrack?.participantId;
        const rec = getFreezeRecovery(participantId);
        // @ts-ignore — requestVideoFrameCallback 은 구형 TS DOM lib 에 없음
        const rvfcSupported = watchedEl && typeof watchedEl.requestVideoFrameCallback === 'function';
        // [간헐 검은 깜빡임 수정] freezeChecker 1단계 재부착(detach→attach)은 srcObject 를 비우므로
        // 그때까지 보이던 마지막 프레임이 검정으로 바뀐다. 재부착 직전 마지막 프레임을 캔버스로 덮어
        // 프레임이 다시 흐를 때까지 유지한다. 픽셀을 읽지 않고 표시만 하므로 canvas taint 무관.
        // ★마지막 프레임은 참가자 레코드(rec.lastFrame)에도 보관한다 — 전체 재접속으로 <video> 가 새로
        // 만들어지면 그 요소엔 덮을 프레임이 없어(videoWidth 0) 종전엔 첫 사이클에만 마스크가 동작했다.
        let freezeMask = null;
        const removeFreezeMask = () => {
            if (freezeMask) {
                freezeMask.parentNode?.removeChild(freezeMask);
                freezeMask = null;
            }
        };
        const captureFrame = () => {
            const v = videoElement.current;
            if (!v || !v.videoWidth || !v.videoHeight)
                return null;
            try {
                const c = document.createElement('canvas');
                c.width = v.videoWidth;
                c.height = v.videoHeight;
                const ctx = c.getContext('2d');
                if (!ctx)
                    return null;
                ctx.drawImage(v, 0, 0, c.width, c.height);
                return c;
            }
            catch (e) {
                return null;
            }
        };
        const showFreezeMask = () => {
            const wrap = fullscreenElement.current;
            if (!wrap || freezeMask)
                return;
            // 현재 요소의 프레임이 있으면 그것을(레코드 갱신), 없으면 레코드의 마지막 프레임을 복제해 쓴다
            let c = captureFrame();
            if (c)
                rec.lastFrame = c;
            else if (rec.lastFrame) {
                try {
                    c = document.createElement('canvas');
                    c.width = rec.lastFrame.width;
                    c.height = rec.lastFrame.height;
                    c.getContext('2d')?.drawImage(rec.lastFrame, 0, 0);
                }
                catch (e) {
                    c = null;
                }
            }
            if (!c)
                return;
            c.style.position = 'absolute';
            c.style.top = '0';
            c.style.left = '0';
            c.style.width = '100%';
            c.style.height = '100%';
            // <video> 와 동일한 letterbox 정합(구형 TS DOM lib 에 objectFit 타입 없음)
            c.style.setProperty('object-fit', 'contain');
            c.style.pointerEvents = 'none';
            c.style.zIndex = '1';
            wrap.appendChild(c);
            freezeMask = c;
        };
        const showTileText = (text) => {
            peerWaitingRef.current = true;
            setPeerWaiting(text);
        };
        const releaseOverlayNow = () => {
            if (loadingShownRef.current) {
                dispatch(toggleLoad(false));
                loadingShownRef.current = false;
            }
        };
        const diag = async (message, data) => {
            try {
                const { fetchErrorData } = await import('../../../../../modules/API/API');
                // senderParticipantId: 어느 송신자(강사) 영상이 멈췄는지 — 여러 학생 로그가 같은 id 로 동시 시작하면 송신 측 사고
                fetchErrorData({ message, data: { ...data, senderParticipantId: participantId, stage: rec.stage, restarts: rec.restarts } });
            }
            catch (e) { /* 진단 로그 실패 무시 */ }
        };
        // 세션 중 복구가 진행 중인 참가자의 새 instance(재접속으로 트랙 교체됨): 첫 프레임까지
        // 정지 프레임 마스크 + 타일 안내로 잇는다. 전역 오버레이는 아래 showLoadTimeout 에서 건너뛴다.
        if (rec.recovering && !props.isPeerStudentTile) {
            showFreezeMask();
            showTileText(rec.stage === 4 ? FREEZE_TEXT_SENDER : FREEZE_TEXT_RECOVERING);
        }
        if (rvfcSupported) {
            const onFrame = () => {
                const now = Date.now();
                lastFrameTs = now;
                rec.stage = 0;
                rec.recovering = false;
                // 마지막 전체 재접속 뒤 충분히 정상이면 누적 상한 초기화(다음 사고에 다시 3회 허용)
                if (rec.restarts > 0 && now - rec.lastRestartAt > FREEZE_RESTART_DECAY_MS)
                    rec.restarts = 0;
                removeFreezeMask(); // 프레임 재개 — 정지 프레임 마스크 즉시 해제
                didFireRef.current = true;
                clearPeerWaiting();
                releaseOverlayNow();
                // @ts-ignore — 체인 재등록(프레임 흐름 지속 관찰)
                rvfcId = watchedEl.requestVideoFrameCallback(onFrame);
            };
            // @ts-ignore
            rvfcId = watchedEl.requestVideoFrameCallback(onFrame);
        }
        // 백그라운드 탭에서는 rVFC 가 스로틀되므로 복귀 시 기준 시각을 리셋해 오탐을 막는다
        const onVisibilityChange = () => {
            if (!document.hidden) {
                lastFrameTs = Date.now();
            }
        };
        document.addEventListener('visibilitychange', onVisibilityChange);
        // ★재생 중 프리즈 감지 + 단계적 복구(참가자 레코드 기반).
        // 게이트: 학생/조교가 보는 스테이지 영상(!muted && isLocalStudent)에만 — 수신 제약(maxHeight:0)으로
        // 정당하게 멎어 있는 타일에 오탐하지 않기 위함. 핀(다른 학생) 타일 제외(상대 기기 문제라 내 재접속으로 안 고쳐짐).
        //
        // 사다리(가벼운 것부터, 각 단계는 프레임이 돌아오면 onFrame 이 즉시 종료):
        //   0→1  5s 무프레임: 트랙 재부착(비용 0) + 정지 프레임 마스크 + "영상 신호를 기다리는 중"
        //   1→2  +5s: 내 JVB 미디어 세션만 재시작(restartMediaSessionsLight — 회의 유지, 퇴장/입장 없음)
        //   2→3  +20s: 전체 재접속(전역 쿨다운 60s, 참가자당 상한 3회)
        //   3→0  +60s: 프레임 미복귀면 재부착부터 다시(상한 내에서)
        //   →4   상한 도달: "강사 측 송출 확인 중" 안내 + 3분마다 가벼운 재시작만(내 수신 경로 이상 대비).
        // 종전(재부착 5s → 즉시 전체 재접속, 인스턴스 지역 카운터)은 송신 측 장애에 60초 주기 무한 재접속
        // 이었고 전역 오버레이가 화면을 계속 덮었다. 송신 측 장애는 강사 클라이언트 자가 복구(senderHealth)가
        // 담당하므로 학생은 기다리며 안내하는 쪽이 방 전체에도 낫다.
        const freezeCheckerActive = rvfcSupported && !props.muted && props.isLocalStudent && !props.isPeerStudentTile;
        let freezeChecker = null;
        if (freezeCheckerActive) {
            freezeChecker = setInterval(() => {
                if (document.hidden)
                    return;
                const now = Date.now();
                if (now - lastFrameTs < FREEZE_DETECT_MS)
                    return; // 프레임 흐르는 중
                rec.recovering = true;
                switch (rec.stage) {
                    case 0: {
                        // 1단계: 로컬 재부착 — srcObject 재설정으로 렌더 파이프라인 리셋
                        // @ts-ignore
                        const jitsiTrack = props.videoTrack?.track;
                        console.warn('[freezeRecovery] 5s 무프레임 — 트랙 재부착 시도');
                        showFreezeMask(); // detach 가 요소를 비우기 전에 마지막 프레임을 덮는다
                        if (jitsiTrack && videoElement.current) {
                            try {
                                jitsiTrack.detach(videoElement.current);
                                jitsiTrack.attach(videoElement.current);
                                videoElement.current.play().catch(() => { });
                            }
                            catch (e) {
                                console.error('[freezeRecovery] 재부착 실패:', e);
                            }
                        }
                        releaseOverlayNow();
                        showTileText(FREEZE_TEXT_WAIT);
                        rec.stage = 1;
                        rec.stageAt = now;
                        return;
                    }
                    case 1: {
                        if (now - rec.stageAt < FREEZE_REATTACH_WAIT_MS)
                            return;
                        // 2단계: 내 미디어 세션만 재시작(회의 유지). 미지원 빌드면 다음 단계로 바로 넘긴다.
                        const light = APP.management?.restartMediaSessionsLight?.();
                        console.warn(`[freezeRecovery] 재부착 무효 — 미디어 세션 재시작(light)${light ? '' : ' 미지원 → 전체 재접속 단계로'}`);
                        diag('freezeRecovery lightRestart', { sinceLastFrameMs: now - lastFrameTs, supported: !!light });
                        showTileText(FREEZE_TEXT_RECOVERING);
                        rec.stage = 2;
                        rec.stageAt = light ? now : now - FREEZE_LIGHT_WAIT_MS;
                        return;
                    }
                    case 2: {
                        if (now - rec.stageAt < FREEZE_LIGHT_WAIT_MS)
                            return;
                        // 3단계: 전체 재접속 — 쿨다운·상한
                        if (rec.restarts >= MAX_FREEZE_RESTARTS) {
                            console.warn('[freezeRecovery] 전체 재접속 상한 도달 — 대기 모드(송신 측 복구 대기)');
                            diag('freezeRecovery limitReached', { restarts: rec.restarts, sinceLastFrameMs: now - lastFrameTs });
                            releaseOverlayNow();
                            showTileText(FREEZE_TEXT_SENDER);
                            rec.stage = 4;
                            rec.stageAt = now;
                            return;
                        }
                        if (now - lastMediaRestartAt < MEDIA_RESTART_COOLDOWN_MS)
                            return; // 전역 쿨다운 대기
                        lastMediaRestartAt = now;
                        rec.restarts += 1;
                        rec.lastRestartAt = now;
                        rec.stage = 3;
                        rec.stageAt = now;
                        releaseOverlayNow();
                        showTileText(FREEZE_TEXT_RECOVERING);
                        console.warn(`[freezeRecovery] 재접속(full) #${rec.restarts} — restartMediaSessions`);
                        diag('freezeRecovery restartMediaSessions', { restarts: rec.restarts, sinceLastFrameMs: now - lastFrameTs });
                        APP.management.restartMediaSessions();
                        return;
                    }
                    case 3: {
                        // 전체 재접속 후 대기. 트랙이 교체되면 새 instance 가 같은 레코드로 이어받는다.
                        if (now - rec.stageAt < FREEZE_FULL_WAIT_MS)
                            return;
                        rec.stage = 0; // 다음 tick 에 재부착부터(상한은 case 2 에서 확인)
                        return;
                    }
                    default: {
                        // 4: 대기 모드 — 프레임이 오면 onFrame 이 0 으로 되돌린다. 저빈도 가벼운 재시작만.
                        if (now - rec.stageAt > FREEZE_IDLE_LIGHT_RETRY_MS) {
                            rec.stageAt = now;
                            console.warn('[freezeRecovery] 대기 모드 — 미디어 세션 재시작(light) 재시도');
                            APP.management?.restartMediaSessionsLight?.();
                        }
                        return;
                    }
                }
            }, 2000);
        }
        // 타이머 발화 시점의 실재생 판정(rVFC 미지원 폴백). MediaStream 은 첫 프레임 이후 readyState 가
        // HAVE_ENOUGH_DATA 에 고정되므로(Chromium 실코드 확인), 재생 중이면 "연결 실패"가 아니다.
        const isActuallyRendering = () => {
            const v = videoElement.current;
            if (!v)
                return false;
            return v.readyState >= 3 && !v.paused && !v.ended;
        };
        // 전역 로딩은 스테이지(교사/화면공유) 영상의 "최초 입장" 대기에만 — 핀(다른 학생) 타일 제외.
        // ★세션 중 복구(이 참가자의 프레임을 본 적 있거나 복구 진행 중)에는 화면 전체를 덮지 않는다:
        //   종전엔 전체 재접속으로 트랙이 교체될 때마다 새 instance 가 1초 뒤 오버레이를 다시 켜서
        //   270초 장애 중 180초를 어두운 화면으로 덮었다. 정지 프레임 마스크 + 타일 안내가 대신한다.
        if (!props.muted && props.isLocalStudent && !props.isPeerStudentTile && !rec.recovering && !rec.lastFrame) {
            showLoadTimeout = setTimeout(() => {
                if (!didFireRef.current && !isActuallyRendering()) {
                    dispatch(toggleLoad(true));
                    loadingShownRef.current = true;
                }
            }, 1000);
        }
        // ★핀(손들기 수락 등 다른 학생) 카메라 타일: 화면 전체를 가리는 전역 로딩 대신
        // 타일 내 "카메라 준비 중" 표시. 상대 태블릿/모바일의 카메라 재획득(수 초) 동안
        // 다른 모든 시청자의 수업 화면을 가리지 않는다. 첫 프레임(rVFC/canplaythrough) 도착 시 해제.
        let peerWaitTimeout = null;
        if (!props.muted && props.isPeerStudentTile) {
            peerWaitTimeout = setTimeout(() => {
                if (!didFireRef.current && !isActuallyRendering()) {
                    peerWaitingRef.current = true;
                    setPeerWaiting('카메라 준비 중...');
                }
            }, 300);
        }
        const releaseOverlay = () => {
            if (loadingShownRef.current) {
                dispatch(toggleLoad(false));
                loadingShownRef.current = false;
            }
        };
        // 첫 프레임 미도착 복구를 "가벼운 것부터, 재연결은 최후"로 처리한다.
        // 과거엔 5초에 곧장 전체 재연결이라, 그냥 느리게 오던 입장(1080 큰 키프레임 + 입장 혼잡)까지
        // 재연결로 갈아엎어 "로딩→검정→로딩→정상" 이중 사이클을 스스로 만들었다(실측 로그).
        let reattachTried = false;
        const handleCanPlayTimeout = async () => {
            if (didFireRef.current || isActuallyRendering()) {
                clearPeerWaiting(); // 재검사 시 이미 렌더 중이면 쿨다운 대기 표시 잔류 차단
                return;
            }
            // 백그라운드 탭은 자동재생/렌더 지연이 정상 — 재접속 사유가 아님.
            // 오버레이는 해제하고(고착 방지) 재검사를 재무장해 포그라운드 복귀 후 다시 판정한다.
            if (document.hidden) {
                releaseOverlay();
                canPlayTimeout = setTimeout(handleCanPlayTimeout, 5000);
                return;
            }
            // freezeChecker(rVFC, 학생 스테이지)가 단계적 복구(재부착→재접속)를 담당하는 경우,
            // 여기선 조기 재연결을 하지 않고 위임한다. 오버레이는 프레임 도착 시 rVFC 가 해제.
            if (freezeCheckerActive) {
                canPlayTimeout = setTimeout(handleCanPlayTimeout, 5000);
                return;
            }
            // --- freezeChecker 미동작 환경(비-rVFC/비-학생) 폴백: 단계적 복구 ---
            // 1단계(가벼운 복구): 전체 재연결 전에 트랙 재부착만 시도하고 프레임 도착을 더 기다린다.
            if (!reattachTried) {
                reattachTried = true;
                const jitsiTrack = props.videoTrack?.track;
                if (jitsiTrack && videoElement.current) {
                    try {
                        jitsiTrack.detach(videoElement.current);
                        jitsiTrack.attach(videoElement.current);
                        videoElement.current.play().catch(() => { });
                    }
                    catch (e) {
                        console.error('[recover] 트랙 재부착 실패:', e);
                    }
                }
                canPlayTimeout = setTimeout(handleCanPlayTimeout, 5000);
                return;
            }
            // 2단계(최후): 재부착으로도 첫 프레임이 안 오면 전체 재연결(전역 쿨다운 준수)
            const now = Date.now();
            const cooldownRemaining = MEDIA_RESTART_COOLDOWN_MS - (now - lastMediaRestartAt);
            if (cooldownRemaining > 0) {
                console.warn('canplay 미발화이나 전체 재접속 쿨다운 중 — restartMediaSessions 생략');
                releaseOverlay();
                // 쿨다운 대기(최대 60초) 동안 검은 타일만 방치하지 않도록 타일 내 대기 표시.
                // 첫 프레임 도착(rVFC onFrame / canplaythrough) 시 clearPeerWaiting 이 해제한다.
                peerWaitingRef.current = true;
                setPeerWaiting('영상 준비 중...');
                canPlayTimeout = setTimeout(handleCanPlayTimeout, cooldownRemaining + 1000);
                return;
            }
            lastMediaRestartAt = now;
            console.warn('첫 프레임 미도착(재부착 후에도) — restartMediaSessions');
            APP.management.restartMediaSessions();
            const jitsiOptions = APP.management?.jitsiConference?.statistics?.options ?? {};
            const { fetchErrorData } = await import('../../../../../modules/API/API');
            await fetchErrorData({
                message: 'restartMediaSessions',
                data: {
                    jitsiOptions
                },
            });
            dispatch(toggleLoad(false));
        };
        // ★핀(다른 학생) 타일은 복구 워치독 미가동: 재부착·전체 재접속 모두 상대 기기 문제를
        // 못 고친다. 상대가 publish 를 마치면 프레임은 자동으로 흐른다(rVFC 가 표시 해제).
        if (!props.isPeerStudentTile) {
            canPlayTimeout = setTimeout(handleCanPlayTimeout, 5000);
        }
        return () => {
            if (canPlayTimeout)
                clearTimeout(canPlayTimeout);
            if (showLoadTimeout)
                clearTimeout(showLoadTimeout);
            if (peerWaitTimeout)
                clearTimeout(peerWaitTimeout);
            if (freezeChecker)
                clearInterval(freezeChecker);
            removeFreezeMask(); // 트랙 교체/언마운트 시 정지 프레임 마스크 잔류 방지
            document.removeEventListener('visibilitychange', onVisibilityChange);
            // @ts-ignore
            if (rvfcId !== null && watchedEl && typeof watchedEl.cancelVideoFrameCallback === 'function') {
                // @ts-ignore
                watchedEl.cancelVideoFrameCallback(rvfcId);
            }
            if (loadingShownRef.current) {
                dispatch(toggleLoad(false));
            }
        };
    }, [props.videoTrack, props.muted, props.isLocalStudent, props.isPeerStudentTile]);
    const handleCanPlayThrough = (e) => {
        didFireRef.current = true;
        clearPeerWaiting();
        dispatch(toggleLoad(false));
        props.eventHandlers?.onCanPlayThrough?.(e);
    };
    return (React.createElement("div", { ref: fullscreenElement, style: { position: 'relative', width: '100%', height: '100%' } },
        React.createElement("video", { onCanPlayThrough: handleCanPlayThrough, className: props.className, ref: videoElement, loop: true, playsInline: true, style: { position: 'relative', width: '100%', height: '100%', objectFit: 'contain' }, controls: false }),
        peerWaiting && (React.createElement("div", { style: {
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(0, 0, 0, 0.55)',
                color: '#fff',
                fontSize: 13,
                // 정지 프레임 마스크(canvas, zIndex 1)가 wrapper 에 나중에 append 되어
                // 같은 z 면 표시를 덮는다 — 마스크(1) < 대기/안내 표시(2) < 워터마크(3)
                zIndex: 2,
                pointerEvents: 'none',
            } }, peerWaiting)),
        props.isFullScreen && (React.createElement(FullScreenBtn, { className: "video_right", video: videoElement.current, handlerFullScreen: props.handlerFullScreen, handlerExitFullScreen: props.handlerExitFullScreen }))));
});
export default Video;
